// Ficha del cliente, parte comercial (pedido de Rodrigo, 2026-10-24: «no está estructurada, no está
// clara»), como las pestañas «Asignaciones» y «Créditos» de la Mantención del Maestro de Random.
//
// Todo a la vista y en recuadros con título. Cada recuadro guarda lo suyo: lo que el usuario puede
// cambiar directo se aplica al tiro; lo que no, queda pedido EN LÍNEA (el «Permiso Modif.» de Random)
// y lo firma un supervisor desde Firmas. La política de cada campo la decide el backend.
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type ReactNode } from 'react';
import { api, nuevaClaveIdempotencia } from '../api/cliente';
import { claves, usePoliticaFicha, useSolicitudesCambio } from '../api/consultas';
import type {
  CampoFicha,
  FichaEntidad,
  ListaPrecio,
  ResultadoCambios,
  Vendedor,
} from '../api/contratos';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { entradaDecimal } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { NOMBRE_CAMPO } from './valoresFicha';

type Relacion = FichaEntidad['empresas'][number];
export type Aplicacion = 'DIRECTA' | 'SOLICITUD' | 'NO_DISPONIBLE' | 'PENDIENTE';
export interface Cambio {
  campo: CampoFicha;
  valor: object;
}

const DIAS = ['L', 'M', 'M', 'J', 'V', 'S', 'D'] as const;
const NOMBRE_DIA = ['lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado', 'domingo'];

/** Las condiciones de pago de MADECI (Rodrigo, 2026-10-24): inmediato o crédito. */
export const CONDICIONES_CLIENTE: Array<[string, string]> = [
  ['contado', 'Efectivo / transferencia'],
  ['cheque', 'Cheque (crédito documentado)'],
  ['credito', 'Crédito (a firma)'],
];

/** Cómo aplica cada campo para este usuario, con lo que ya está pedido. */
function usePolitica(empresaId: string, entidadId: string) {
  const politica = usePoliticaFicha(empresaId, entidadId);
  const solicitudes = useSolicitudesCambio(empresaId, { entidadId, estado: 'PENDIENTE' });
  const pendientes = new Set((solicitudes.data ?? []).flatMap((s) => s.campos.map((c) => c.campo)));
  return (campo: CampoFicha): Aplicacion =>
    pendientes.has(campo)
      ? 'PENDIENTE'
      : (politica.data?.find((p) => p.campo === campo)?.aplicacion ?? 'NO_DISPONIBLE');
}

export function Marca({ a }: { a: Aplicacion }) {
  return a === 'DIRECTA' ? (
    <span className="insignia tono-ok" title="Lo cambias directo (queda en la auditoría)">
      directo
    </span>
  ) : a === 'SOLICITUD' ? (
    <span className="insignia tono-aviso" title="Se pide en línea; lo autoriza un supervisor">
      con autorización
    </span>
  ) : a === 'PENDIENTE' ? (
    <span className="insignia tono-aviso" title="Ya hay un cambio pedido esperando firma">
      cambio pendiente
    </span>
  ) : (
    <span className="insignia tono-neutro">sin permiso</span>
  );
}

function Campo({
  etiqueta,
  marca,
  ayuda,
  children,
}: {
  etiqueta: string;
  marca?: Aplicacion;
  ayuda?: string;
  children: ReactNode;
}) {
  return (
    <div className="campo">
      <span className="etiqueta">
        {etiqueta} {marca && <Marca a={marca} />}
      </span>
      {children}
      {ayuda && <span className="ayuda">{ayuda}</span>}
    </div>
  );
}

/**
 * Sin motivo obligatorio (Rodrigo: trazabilidad, no motivos): sin comentario, la solicitud dice de
 * dónde salió. El backend exige al menos 3 letras.
 */
const motivoOVacio = (m: string) =>
  m.trim().length >= 3 ? m.trim() : 'Pedido desde la ficha del cliente';

/**
 * Guardar un recuadro: lo directo (PATCH /comercial) y los campos con política (POST /cambios). Si
 * algo va con autorización, ofrece un comentario opcional (lo ve quien firma).
 */
function useGuardarSeccion(empresaId: string, entidadId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      cambios,
      directo,
      motivo,
    }: {
      cambios: Cambio[];
      directo?: (() => Promise<unknown>) | undefined;
      motivo: string;
    }) => {
      if (directo) await directo();
      if (!cambios.length) return null;
      return api.post<ResultadoCambios>(
        `/entidades/${entidadId}/cambios`,
        { empresaId, motivo, campos: cambios },
        { idempotencia: nuevaClaveIdempotencia('ficha-seccion') },
      );
    },
    onSuccess: () =>
      Promise.all([
        qc.invalidateQueries({ queryKey: claves.cliente(empresaId, entidadId) }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'solicitudes-cambio'] }),
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'contexto-comercial'] }),
        // Completado un dato que faltaba, cambiarlo de nuevo ya va por firma.
        qc.invalidateQueries({ queryKey: ['emp', empresaId, 'politica-ficha'] }),
      ]),
  });
}

function BotonGuardar({
  empresaId,
  entidadId,
  cambios,
  aplicacion,
  directo,
  hayDirecto,
  problemas,
  etiqueta,
}: {
  empresaId: string;
  entidadId: string;
  cambios: Cambio[];
  aplicacion: (c: CampoFicha) => Aplicacion;
  directo?: () => Promise<unknown>;
  hayDirecto?: boolean;
  problemas: string[];
  etiqueta: string;
}) {
  const guardar = useGuardarSeccion(empresaId, entidadId);
  const [motivo, setMotivo] = useState<string | null>(null);
  const [resultado, setResultado] = useState<ResultadoCambios | null>(null);
  const piden = cambios.filter((c) => aplicacion(c.campo) === 'SOLICITUD');
  const hay = cambios.length > 0 || !!hayDirecto;
  const ejecutar = (m: string) =>
    guardar.mutate(
      { cambios, directo: hayDirecto ? directo : undefined, motivo: m },
      {
        onSuccess: (r) => {
          setMotivo(null);
          setResultado(r);
        },
      },
    );
  return (
    <div className="acciones-ficha">
      {problemas.length > 0 && <span className="texto-malo">{problemas.join(' · ')}</span>}
      {resultado && resultado.pendientes.length > 0 && (
        <span className="texto-aviso" role="status">
          Pedido en línea: {resultado.pendientes.map((c) => NOMBRE_CAMPO[c]).join(', ')}. Queda en
          Firmas hasta que un supervisor lo autorice.
        </span>
      )}
      {guardar.isSuccess && (!resultado || !resultado.pendientes.length) && (
        <span className="texto-ok" role="status">
          Guardado.
        </span>
      )}
      <button
        type="button"
        className="primario"
        disabled={!hay || problemas.length > 0 || guardar.isPending}
        onClick={() => (piden.length ? setMotivo('') : ejecutar('Edición de la ficha del cliente'))}
      >
        {guardar.isPending
          ? 'Guardando…'
          : piden.length
            ? `${etiqueta} · pedir autorización`
            : etiqueta}
      </button>
      {guardar.isError && <PanelError error={guardar.error} />}
      {motivo !== null && (
        <Modal
          titulo="Pedir autorización en línea"
          ancho="angosto"
          alCerrar={() => setMotivo(null)}
          pie={
            <>
              <button type="button" onClick={() => setMotivo(null)}>
                Volver
              </button>
              <button
                type="button"
                className="primario"
                disabled={guardar.isPending}
                onClick={() => ejecutar(motivoOVacio(motivo))}
              >
                Enviar a Firmas
              </button>
            </>
          }
        >
          <p>
            Esto necesita la autorización de un supervisor:{' '}
            <b>{piden.map((c) => NOMBRE_CAMPO[c.campo]).join(', ')}</b>. Mientras no se firme, el
            cliente sigue como está.
          </p>
          <label className="campo">
            <span className="etiqueta">Comentario para quien autoriza (opcional)</span>
            <input
              aria-label="Motivo de la solicitud"
              data-autofoco
              maxLength={500}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.toUpperCase())}
              onKeyDown={(e) => {
                if (e.key === 'Enter') ejecutar(motivoOVacio(motivo));
              }}
            />
          </label>
        </Modal>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- asignaciones

type Rol = 'cliente' | 'proveedor' | 'ambos';
const rolDe = (rel: { es_cliente?: boolean; es_proveedor?: boolean }): Rol =>
  rel.es_proveedor ? (rel.es_cliente === false ? 'proveedor' : 'ambos') : 'cliente';

/** Lo que se edita en «Asignaciones», en la ficha y al crear el cliente. */
export interface ValoresAsignaciones {
  vendedor: string;
  lista: string;
  rol: Rol;
  documento: string;
  dias: string;
  cobrador: string;
  segmento: string;
  clasificacion: string;
}

/** Los valores de la relación con la empresa; sin relación (cliente nuevo), todo vacío. */
export const asignacionesDe = (rel: Relacion | null): ValoresAsignaciones => ({
  vendedor: rel?.vendedor_id ?? '',
  lista: rel?.lista_precio_id ?? '',
  rol: rel ? rolDe(rel) : 'cliente',
  documento: rel?.documento_por_defecto ?? '',
  dias: rel?.dias_visita ?? '-------',
  cobrador: rel?.cobrador_id ?? '',
  segmento: rel?.segmento ?? '',
  clasificacion: rel?.clasificacion ?? '',
});

/** Lo que el usuario cambia directo en «Asignaciones» (lo con política lo dice `aplicacion`). */
export function usePermisosAsignaciones() {
  const { puede } = useSesion();
  const editable = puede('entidades.gestionar');
  // El documento fijo lo asigna quien aprueba cambios de la ficha (Rodrigo, 2026-10-24) o tiene el
  // permiso propio (el call center, 2026-10-27).
  const documentoEditable =
    editable && (puede('entidades.ficha.aprobar_cambio') || puede('entidades.ficha.documento'));
  return { editable, documentoEditable };
}

/** Vendedor, cobrador, lista de precios, documento y visitas (la «Asignaciones» de Random). */
export function Asignaciones({
  empresaId,
  ficha,
  rel,
  vendedores,
  listas,
}: {
  empresaId: string;
  ficha: FichaEntidad;
  rel: Relacion;
  vendedores: Vendedor[];
  listas: ListaPrecio[];
}) {
  const aplicacion = usePolitica(empresaId, ficha.entidad.id);
  const permisos = usePermisosAsignaciones();
  const inicial = asignacionesDe(rel);
  const [f, setF] = useState(inicial);
  const poner = (c: Partial<typeof f>) => setF((x) => ({ ...x, ...c }));
  const cambios: Cambio[] = [];
  if (f.vendedor !== inicial.vendedor)
    cambios.push({ campo: 'vendedor', valor: { vendedorId: f.vendedor || null } });
  if (f.lista && f.lista !== inicial.lista)
    cambios.push({ campo: 'lista_precio', valor: { listaPrecioId: f.lista } });
  const directos = (
    ['rol', 'documento', 'dias', 'cobrador', 'segmento', 'clasificacion'] as const
  ).some((k) => f[k] !== inicial[k]);
  return (
    <div className="ficha-seccion">
      <CamposAsignaciones
        f={f}
        poner={poner}
        vendedores={vendedores}
        listas={listas}
        aplicacion={aplicacion}
        {...permisos}
      />
      <BotonGuardar
        empresaId={empresaId}
        entidadId={ficha.entidad.id}
        cambios={cambios}
        aplicacion={aplicacion}
        hayDirecto={directos}
        directo={() =>
          api.patch(`/entidades/${ficha.entidad.id}/comercial`, {
            empresaId,
            version: rel.version ?? 1,
            esCliente: f.rol !== 'proveedor',
            esProveedor: f.rol !== 'cliente',
            documentoPorDefecto: f.documento || null,
            costoLogistico: !!rel.costo_logistico,
            diasVisita: f.dias.includes('X') ? f.dias : null,
            cobradorId: f.cobrador || null,
            segmento: f.segmento.trim() || null,
            clasificacion: f.clasificacion.trim() || null,
          })
        }
        problemas={[]}
        etiqueta="Guardar asignaciones"
      />
    </div>
  );
}

/** Los recuadros de «Asignaciones». Al crear el cliente el tipo de entidad va en la información general. */
export function CamposAsignaciones({
  f,
  poner,
  vendedores,
  listas,
  aplicacion,
  editable,
  documentoEditable,
  conTipo = true,
}: {
  f: ValoresAsignaciones;
  poner: (c: Partial<ValoresAsignaciones>) => void;
  vendedores: Vendedor[];
  listas: ListaPrecio[];
  aplicacion: (c: CampoFicha) => Aplicacion;
  editable: boolean;
  documentoEditable: boolean;
  conTipo?: boolean;
}) {
  const bloqueado = (c: CampoFicha) => ['NO_DISPONIBLE', 'PENDIENTE'].includes(aplicacion(c));
  const venta = listas.filter((l) => l.uso === 'venta');
  return (
    <>
      <fieldset className="recuadro-ficha">
        <legend>Vendedor y lista de precios</legend>
        <div className="ficha-grilla">
          <Campo etiqueta="Vendedor asignado" marca={aplicacion('vendedor')}>
            <select
              aria-label="Vendedor asignado"
              disabled={bloqueado('vendedor')}
              value={f.vendedor}
              onChange={(e) => poner({ vendedor: e.target.value })}
            >
              <option value="">Sin vendedor</option>
              {vendedores
                .filter((v) => v.activo || v.id === f.vendedor)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.codigo} · {v.nombre}
                  </option>
                ))}
            </select>
          </Campo>
          <Campo etiqueta="Precios de venta" marca={aplicacion('lista_precio')}>
            <select
              aria-label="Precios de venta"
              disabled={bloqueado('lista_precio')}
              value={f.lista}
              onChange={(e) => poner({ lista: e.target.value })}
            >
              {!f.lista && <option value="">Sin lista</option>}
              {venta.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.codigo} · {l.nombre}
                </option>
              ))}
            </select>
          </Campo>
        </div>
      </fieldset>
      <fieldset className="recuadro-ficha">
        <legend>Cobranza y visitas</legend>
        <div className="ficha-grilla">
          <Campo etiqueta="Cobrador asignado">
            <select
              aria-label="Cobrador"
              disabled={!editable}
              value={f.cobrador}
              onChange={(e) => poner({ cobrador: e.target.value })}
            >
              <option value="">Sin cobrador</option>
              {vendedores
                .filter((v) => v.activo || v.id === f.cobrador)
                .map((v) => (
                  <option key={v.id} value={v.id}>
                    {v.codigo} · {v.nombre}
                  </option>
                ))}
            </select>
          </Campo>
          <Campo etiqueta="Días de visita / cobranza">
            <span className="dias-visita" role="group" aria-label="Días de visita">
              {DIAS.map((d, i) => (
                <label key={i} title={NOMBRE_DIA[i]}>
                  <input
                    type="checkbox"
                    aria-label={`Visita el ${NOMBRE_DIA[i]}`}
                    disabled={!editable}
                    checked={f.dias[i] === 'X'}
                    onChange={(e) =>
                      poner({
                        dias: f.dias
                          .split('')
                          .map((c, j) => (j === i ? (e.target.checked ? 'X' : '-') : c))
                          .join(''),
                      })
                    }
                  />
                  {d}
                </label>
              ))}
            </span>
          </Campo>
        </div>
      </fieldset>
      <fieldset className="recuadro-ficha">
        <legend>Venta</legend>
        <div className="ficha-grilla">
          {conTipo && (
            <Campo
              etiqueta="Tipo de entidad"
              ayuda="Solo los clientes aparecen en ventas y cobranza; solo los proveedores en compras."
            >
              <select
                aria-label="Tipo de entidad"
                disabled={!editable}
                value={f.rol}
                onChange={(e) => poner({ rol: e.target.value as Rol })}
              >
                <option value="cliente">Cliente</option>
                <option value="proveedor">Proveedor</option>
                <option value="ambos">Cliente y proveedor</option>
              </select>
            </Campo>
          )}
          <Campo
            etiqueta="Documento a emitir"
            ayuda={
              documentoEditable
                ? 'Fijo para este cliente; si no, al vender se elige factura o boleta.'
                : 'Lo fija quien aprueba cambios de la ficha.'
            }
          >
            <select
              aria-label="Documento a emitir"
              disabled={!documentoEditable}
              value={f.documento}
              onChange={(e) => poner({ documento: e.target.value })}
            >
              <option value="">Factura o boleta (al vender)</option>
              <option value="FACTURA">Siempre factura</option>
              <option value="BOLETA">Siempre boleta</option>
            </select>
          </Campo>
          <Campo etiqueta="Segmento">
            <input
              aria-label="Segmento"
              disabled={!editable}
              maxLength={40}
              value={f.segmento}
              onChange={(e) => poner({ segmento: e.target.value.toUpperCase() })}
            />
          </Campo>
          <Campo etiqueta="Clasificación">
            <input
              aria-label="Clasificación"
              disabled={!editable}
              maxLength={40}
              value={f.clasificacion}
              onChange={(e) => poner({ clasificacion: e.target.value.toUpperCase() })}
            />
          </Campo>
        </div>
      </fieldset>
    </>
  );
}

// ---------------------------------------------------------------- crédito

/** Condición de pago, plazo, línea y bloqueo (la «Créditos» de Random). Todo con su política. */
export function Credito({
  empresaId,
  ficha,
  rel,
}: {
  empresaId: string;
  ficha: FichaEntidad;
  rel: Relacion;
}) {
  const aplicacion = usePolitica(empresaId, ficha.entidad.id);
  const inicial = creditoDe(rel);
  const [f, setF] = useState(inicial);
  const poner = (c: Partial<typeof f>) => setF((x) => ({ ...x, ...c }));
  const cambios = cambiosCredito(f, inicial);
  return (
    <div className="ficha-seccion">
      <CamposCredito f={f} poner={poner} aplicacion={aplicacion} />
      <BotonGuardar
        empresaId={empresaId}
        entidadId={ficha.entidad.id}
        cambios={cambios}
        aplicacion={aplicacion}
        problemas={cambios.length ? problemasCredito(f) : []}
        etiqueta="Guardar crédito"
      />
    </div>
  );
}

/** Lo que se edita en «Crédito», en la ficha y al crear el cliente. */
export interface ValoresCredito {
  condicion: string;
  plazo: string;
  modalidad: string;
  morosidad: string;
  linea: string;
  bloqueado: boolean;
  motivoBloqueo: string;
}

/** Los valores de la relación con la empresa; sin relación (cliente nuevo), al contado sin línea. */
export function creditoDe(rel: Relacion | null): ValoresCredito {
  const r = rel as (Relacion & { credito_modalidad?: string; dias_morosidad?: number }) | null;
  return {
    condicion: r?.condicion_pago ?? 'contado',
    plazo: String(r?.plazo_dias ?? 0),
    modalidad: r?.credito_modalidad ?? 'NORMAL',
    morosidad: String(r?.dias_morosidad ?? 0),
    linea: r?.linea_credito ? String(Math.round(Number(r.linea_credito))) : '',
    bloqueado: !!r?.bloqueado,
    motivoBloqueo: r?.motivo_bloqueo ?? '',
  };
}

export const pagoInmediato = (condicion: string) =>
  condicion === 'contado' || condicion === 'transferencia';

/** Los cambios con política de la sección, en el orden en que el backend los acepta. */
export function cambiosCredito(f: ValoresCredito, inicial: ValoresCredito): Cambio[] {
  const inmediato = pagoInmediato(f.condicion);
  const cambios: Cambio[] = [];
  const condicion: Cambio | null =
    f.condicion !== inicial.condicion ||
    f.plazo !== inicial.plazo ||
    f.modalidad !== inicial.modalidad ||
    f.morosidad !== inicial.morosidad
      ? {
          campo: 'condicion_pago',
          valor: {
            condicion: f.condicion,
            plazoDias: inmediato ? 0 : Number(f.plazo || 0),
            modalidad: f.condicion === 'credito' ? f.modalidad : 'NORMAL',
            diasMorosidad: inmediato ? 0 : Number(f.morosidad || 0),
          },
        }
      : null;
  const linea: Cambio | null =
    f.linea !== inicial.linea
      ? {
          campo: 'linea_credito',
          valor: { valor: f.linea.trim() ? String(entradaDecimal(f.linea, 0)) : null },
        }
      : null;
  // Pasar a pago inmediato: primero se quita la línea; pasar a crédito: primero la condición.
  for (const c of inmediato ? [linea, condicion] : [condicion, linea]) if (c) cambios.push(c);
  if (f.bloqueado !== inicial.bloqueado || f.motivoBloqueo !== inicial.motivoBloqueo)
    cambios.push({
      campo: 'estado_comercial',
      valor: { bloqueado: f.bloqueado, ...(f.bloqueado ? { motivo: f.motivoBloqueo.trim() } : {}) },
    });
  return cambios;
}

export function problemasCredito(f: ValoresCredito): string[] {
  const inmediato = pagoInmediato(f.condicion);
  return [
    ...(!inmediato && !/^\d{1,3}$/.test(f.plazo) ? ['El plazo va en días (0 a 365)'] : []),
    ...(!inmediato && !/^\d{1,3}$/.test(f.morosidad)
      ? ['La morosidad permitida va en días (0 a 365)']
      : []),
    ...(f.linea.trim() && !entradaDecimal(f.linea, 0) ? ['La línea es un monto en pesos'] : []),
    ...(inmediato && f.linea.trim()
      ? ['Efectivo / transferencia no lleva línea de crédito: déjala vacía']
      : []),
    ...(f.bloqueado && f.motivoBloqueo.trim().length < 3 ? ['Bloquear exige motivo'] : []),
  ];
}

/** Los recuadros de «Crédito». Un cliente nuevo no nace bloqueado: al crearlo va sin «Bloqueo». */
export function CamposCredito({
  f,
  poner,
  aplicacion,
  conBloqueo = true,
}: {
  f: ValoresCredito;
  poner: (c: Partial<ValoresCredito>) => void;
  aplicacion: (c: CampoFicha) => Aplicacion;
  conBloqueo?: boolean;
}) {
  const bloqueado = (c: CampoFicha) => ['NO_DISPONIBLE', 'PENDIENTE'].includes(aplicacion(c));
  const inmediato = pagoInmediato(f.condicion);
  const opciones = CONDICIONES_CLIENTE.some(([v]) => v === f.condicion)
    ? CONDICIONES_CLIENTE
    : [...CONDICIONES_CLIENTE, [f.condicion, f.condicion] as [string, string]];
  return (
    <>
      <fieldset className="recuadro-ficha">
        <legend>Condición de pago</legend>
        <div className="ficha-grilla">
          <Campo etiqueta="Condición" marca={aplicacion('condicion_pago')}>
            <select
              aria-label="Condición de pago"
              disabled={bloqueado('condicion_pago')}
              value={f.condicion}
              onChange={(e) => poner({ condicion: e.target.value })}
            >
              {opciones.map(([v, n]) => (
                <option key={v} value={v}>
                  {n}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            etiqueta="Días de plazo"
            ayuda={
              inmediato ? 'Pago inmediato: sin plazo.' : 'Días desde la emisión al vencimiento.'
            }
          >
            <input
              aria-label="Días de plazo"
              inputMode="numeric"
              disabled={bloqueado('condicion_pago') || inmediato}
              value={inmediato ? '0' : f.plazo}
              onChange={(e) => poner({ plazo: e.target.value.replace(/\D/g, '') })}
            />
          </Campo>
        </div>
        {f.condicion === 'credito' && (
          <div className="ficha-grilla">
            <Campo
              etiqueta="Modalidad del crédito"
              ayuda={
                f.modalidad === 'SIN_CREDITO_SOBRE_CREDITO'
                  ? 'La línea es el máximo de cada pedido. Con una factura pendiente no sale otro crédito (se pide autorización o se cobra contra entrega).'
                  : f.modalidad === 'SIN_CONTROL'
                    ? 'Empresa relacionada: compra a crédito sin control de línea, deuda ni morosidad, y sus pedidos del portal B2B entran con esta condición.'
                    : 'Puede tener varias facturas a crédito mientras la deuda quepa en la línea.'
              }
            >
              <select
                aria-label="Modalidad del crédito"
                disabled={bloqueado('condicion_pago')}
                value={f.modalidad}
                onChange={(e) => poner({ modalidad: e.target.value })}
              >
                <option value="NORMAL">Normal · permite crédito sobre crédito</option>
                <option value="SIN_CREDITO_SOBRE_CREDITO">
                  Especial · sin crédito sobre crédito
                </option>
                <option value="SIN_CONTROL">Relacionada · sin control de crédito</option>
              </select>
            </Campo>
          </div>
        )}
      </fieldset>
      <fieldset className="recuadro-ficha">
        <legend>Línea de crédito</legend>
        <div className="ficha-grilla">
          <Campo
            etiqueta="Crédito total"
            marca={aplicacion('linea_credito')}
            ayuda="Cupo máximo de deuda del cliente (cheques en cartera y facturas por pagar)."
          >
            <input
              aria-label="Línea de crédito"
              inputMode="numeric"
              placeholder="sin línea"
              disabled={bloqueado('linea_credito')}
              value={f.linea}
              onChange={(e) => poner({ linea: e.target.value.replace(/[^\d]/g, '') })}
            />
          </Campo>
          <Campo
            etiqueta="Días de morosidad permitida"
            ayuda="Vencido hasta estos días solo avisa; pasado eso, la venta a crédito se bloquea y se puede pedir autorización."
          >
            <input
              aria-label="Días de morosidad permitida"
              inputMode="numeric"
              disabled={bloqueado('condicion_pago') || inmediato}
              value={inmediato ? '0' : f.morosidad}
              onChange={(e) => poner({ morosidad: e.target.value.replace(/\D/g, '') })}
            />
          </Campo>
        </div>
      </fieldset>
      {conBloqueo && (
        <fieldset className="recuadro-ficha">
          <legend>Bloqueo</legend>
          <div className="ficha-grilla">
            <Campo etiqueta="Bloqueado para venta" marca={aplicacion('estado_comercial')}>
              <label className="opcion-linea">
                <input
                  type="checkbox"
                  aria-label="Bloqueado para venta"
                  disabled={bloqueado('estado_comercial')}
                  checked={f.bloqueado}
                  onChange={(e) => poner({ bloqueado: e.target.checked })}
                />
                {f.bloqueado ? 'Sí, no se le vende' : 'No'}
              </label>
            </Campo>
            {f.bloqueado && (
              <Campo etiqueta="Motivo del bloqueo">
                <input
                  aria-label="Motivo del bloqueo"
                  disabled={bloqueado('estado_comercial')}
                  maxLength={300}
                  value={f.motivoBloqueo}
                  onChange={(e) => poner({ motivoBloqueo: e.target.value.toUpperCase() })}
                />
              </Campo>
            )}
          </div>
        </fieldset>
      )}
    </>
  );
}
